import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography } from "@mui/material";
import { BASE_URL } from "../config/config";

// The cancellation ladder and owner-claim window as the admin has set them,
// shown to tenants (before they pay) and owners (on their wallet). Pass `steps`
// to show a specific booking's own ladder instead of the current one.

export const useCancellationPolicy = () => {
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    let live = true;
    axios
      .get(`${BASE_URL}/settings/cancellation-policy`)
      .then((res) => live && setPolicy(res.data?.data || null))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return policy;
};

const tone = (percent) =>
  percent === 100
    ? { bg: "#e8f8f5", color: "#117a65" }
    : percent > 0
    ? { bg: "#fef9e7", color: "#b7950b" }
    : { bg: "#fadbd8", color: "#78281f" };

export default function CancellationPolicyBox({ steps: given, dense }) {
  const current = useCancellationPolicy();
  const steps = given || current?.steps;
  if (!steps) return null;

  return (
    <Box>
      {steps.map((s, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: i === steps.length - 1 ? "none" : "1px solid #f0f0f0",
            py: dense ? 0.6 : 1,
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {s.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.expired
                ? "No refund. The owner's payment is no longer affected by a cancellation."
                : s.percent === 100
                ? "Full refund — everything you paid comes back."
                : s.percent === 0
                ? "No refund."
                : `${100 - s.percent}% of the advance is deducted. OTS's commission and the processing fee are not refunded.`}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: tone(s.percent).bg,
              color: tone(s.percent).color,
              px: 1.5,
              py: 0.3,
              borderRadius: 5,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {s.expired ? "Non-refundable" : s.percent === 100 ? "100% refund" : `${s.percent}% of advance`}
          </Box>
        </Box>
      ))}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Once you have moved in, the booking can no longer be cancelled for a refund.
      </Typography>
    </Box>
  );
}
